import { useSyncExternalStore } from 'react';

/**
 * The user's primary music service, chosen in Settings.
 *
 * Display-layer only: this never reaches the API, the analyzer or the
 * enricher. Items keep resolving every provider link they can; this
 * preference decides which of those links a surface renders and in what
 * order.
 */
export const MUSIC_SERVICES = {
  APPLE: 'apple',
  SPOTIFY: 'spotify',
  BOTH: 'both',
  NONE: 'none',
};

export const MUSIC_SERVICE_OPTIONS = [
  {
    value: MUSIC_SERVICES.APPLE,
    label: 'Apple Music',
    description: 'Only Apple Music links are shown.',
  },
  {
    value: MUSIC_SERVICES.SPOTIFY,
    label: 'Spotify',
    description: 'Only Spotify links are shown.',
  },
  {
    value: MUSIC_SERVICES.BOTH,
    label: 'Both',
    description: 'Apple Music first, then Spotify.',
  },
  {
    value: MUSIC_SERVICES.NONE,
    label: "I don't have a music service",
    description: 'Show YouTube, SoundCloud and similar links instead.',
  },
];

const STORAGE_KEY = 'collections_music_service';

// `both` keeps the pre-preference behaviour (Apple then Spotify) for anyone
// who never opens Settings.
const DEFAULT_SERVICE = MUSIC_SERVICES.BOTH;

const VALID_SERVICES = new Set(Object.values(MUSIC_SERVICES));

function readStoredService() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return VALID_SERVICES.has(stored) ? stored : DEFAULT_SERVICE;
  } catch {
    return DEFAULT_SERVICE;
  }
}

// Module-level store rather than a context provider: every card on the page
// has to re-render the moment Settings changes the choice, and this keeps
// that wiring out of main.jsx and out of every test's render tree.
let currentService = readStoredService();
const listeners = new Set();

export function getMusicService() {
  return currentService;
}

export function setMusicService(service) {
  if (!VALID_SERVICES.has(service) || service === currentService) return;
  currentService = service;
  try {
    localStorage.setItem(STORAGE_KEY, service);
  } catch {
    // Private browsing or blocked storage: the choice still applies for this session.
  }
  listeners.forEach((listener) => listener());
}

// Each tab holds its own copy of this module, so a change made in a Settings
// tab would otherwise leave an open gallery tab filtering on a stale value
// until it reloaded.
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (event) => {
    if (event.key !== STORAGE_KEY) return;
    const next = readStoredService();
    if (next === currentService) return;
    currentService = next;
    listeners.forEach((listener) => listener());
  });
}

function subscribe(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useMusicService() {
  return useSyncExternalStore(subscribe, getMusicService, getMusicService);
}

/**
 * Filter and order an item's (or enrichment's) music links for the chosen
 * service.
 *
 *  - `apple`/`spotify`: only that service's link; the other is hidden.
 *  - `both`: Apple Music first, Spotify second.
 *  - `none`: neither service; the non-subscription links (YouTube,
 *    SoundCloud, …) are shown instead.
 *
 * `otherLinks` are only ever rendered for `none` — under any of the three
 * service choices the point of the setting is that the chosen service's
 * link is the one to click, so an item with no link on that service shows
 * no music link at all rather than quietly substituting a different source.
 *
 * Returns `[{ provider, label, url }]`, empty when there's nothing to show.
 */
export function orderMusicLinks(
  { appleMusicUrl, spotifyUrl, otherLinks = [] } = {},
  service = getMusicService()
) {
  const apple = appleMusicUrl
    ? [{ provider: 'apple', label: 'Apple Music', url: appleMusicUrl }]
    : [];
  const spotify = spotifyUrl
    ? [{ provider: 'spotify', label: 'Spotify', url: spotifyUrl }]
    : [];

  switch (service) {
    case MUSIC_SERVICES.APPLE:
      return apple;
    case MUSIC_SERVICES.SPOTIFY:
      return spotify;
    case MUSIC_SERVICES.NONE:
      return otherLinks;
    default:
      return [...apple, ...spotify];
  }
}

/**
 * Ordered music links for the currently selected service. Hook form so a
 * card re-renders when the Settings choice changes.
 */
export function useMusicLinks(links) {
  const service = useMusicService();
  return orderMusicLinks(links, service);
}
